import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * PremiumBackdrop
 * Site-wide premium background layer. Two variants:
 *  - "dark" (default) - champagne grid on black + warm radial glows + sweeping band
 *  - "light" - soft brass grid on cream + warm-ivory radial glows + soft sweeping band
 *
 * Drop as the FIRST child of a page's root wrapper. It is position:absolute
 * so it fills the parent (which should be position:relative + overflow:hidden).
 *
 * Pass `fixed` to render as a fixed viewport layer instead.
 */
export default function PremiumBackdrop({ fixed = false, intensity = 1, variant = "dark" }) {
  const reduced = useReducedMotion();
  const positionClass = fixed
    ? "fixed inset-0 pointer-events-none overflow-hidden z-0"
    : "absolute inset-0 pointer-events-none overflow-hidden";

  const isLight = variant === "light";

  // Grid tuning per variant.
  const gridLine = isLight ? "rgba(140,110,60,0.45)" : "rgba(201,169,97,0.7)";
  const gridOpacity = (isLight ? 0.05 : 0.06) * intensity;
  const gridSize = "48px";

  // Radial glow colors.
  const glowA = isLight
    ? "radial-gradient(circle, rgba(201,169,97,0.10) 0%, rgba(201,169,97,0) 65%)"
    : "radial-gradient(circle, rgba(201,169,97,0.12) 0%, rgba(201,169,97,0) 65%)";
  const glowB = isLight
    ? "radial-gradient(circle, rgba(232,201,145,0.09) 0%, rgba(232,201,145,0) 70%)"
    : "radial-gradient(circle, rgba(232,201,145,0.08) 0%, rgba(0,0,0,0) 70%)";

  // Sweeping band opacity (softer on light to avoid muddying cream).
  const bandOpacity = (isLight ? 0.06 : 0.10) * intensity;

  return (
    <div aria-hidden className={positionClass}>
      {/* Grid */}
      <div
        className="absolute inset-0"
        style={{
          opacity: gridOpacity,
          backgroundImage: `linear-gradient(${gridLine} 1px, transparent 1px), linear-gradient(90deg, ${gridLine} 1px, transparent 1px)`,
          backgroundSize: `${gridSize} ${gridSize}`,
        }}
      />

      {/* Top-left radial glow */}
      <div
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full"
        style={{ background: glowA, filter: "blur(20px)" }}
      />

      {/* Bottom-right radial glow */}
      <div
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full"
        style={{ background: glowB, filter: "blur(24px)" }}
      />

      {/* Sweeping horizontal glow band */}
      {!reduced && (
        <motion.div
          className="absolute left-0 right-0 h-24"
          style={{
            background: `linear-gradient(180deg, transparent 0%, rgba(201,169,97,${bandOpacity}) 50%, transparent 100%)`,
          }}
          initial={{ y: "-20%" }}
          animate={{ y: "120vh" }}
          transition={{
            duration: 8,
            ease: "linear",
            repeat: Infinity,
            repeatDelay: 2,
          }}
        />
      )}
    </div>
  );
}
